import {
  DEFAULT_REPORT_COLUMNS,
  DEFAULT_REPORT_SORT,
  isReportColumnKey,
  isReportLifecycleState,
  isReportScanMethod,
  type ReportColumnKey,
  type ReportDateSelection,
  type ReportScanFilterSets,
  type ReportScanLifecycleState,
  type ReportScanMethod,
  type ReportScanSort,
} from "./report-scan-filters";

export interface ReportSavedView {
  readonly viewRef: string;
  readonly viewId: string | null;
  readonly name: string;
  readonly filters: ReportScanFilterSets;
  readonly isDefault: boolean;
  readonly updatedAt: string;
}

export type ReportSavedViewList =
  | { readonly status: "ok"; readonly views: readonly ReportSavedView[] }
  | { readonly status: "unavailable"; readonly reason: "support_session" | "unavailable" };

export type SaveReportViewResult =
  | { readonly status: "ok"; readonly view: ReportSavedView }
  | {
      readonly status: "refused";
      readonly reason:
        | "name_taken"
        | "view_limit"
        | "payload_too_large"
        | "unknown_column"
        | "unknown_filter_value"
        | "too_many_sites";
    }
  | { readonly status: "unavailable"; readonly reason: "support_session" | "unavailable" };

export type DeleteReportViewResult =
  | { readonly status: "ok" }
  | { readonly status: "unavailable"; readonly reason: "support_session" | "unavailable" };

export type ReportLabelValidation =
  | { readonly status: "ok" }
  | {
      readonly status: "stale";
      readonly unmatched: readonly {
        readonly family: string;
        readonly label: string;
      }[];
    }
  | { readonly status: "unavailable"; readonly reason: "support_session" | "unavailable" };

type RpcClient = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: unknown }>;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function parseDateSelection(value: unknown): ReportDateSelection | null {
  const record = asRecord(value);
  if (record === null) return null;
  const preset = asString(record.preset);
  if (
    preset === "today" ||
    preset === "last_7_days" ||
    preset === "last_30_days" ||
    preset === "last_90_days"
  ) {
    return { preset };
  }
  if (preset === "custom") {
    const fromDay = asString(record.fromDay);
    const toDay = asString(record.toDay);
    if (fromDay === null || toDay === null) return null;
    return { preset: "custom", fromDay, toDay };
  }
  return null;
}

function parseSort(value: unknown): ReportScanSort | null {
  const record = asRecord(value);
  if (record === null) return null;
  const column = asString(record.column);
  const direction = asString(record.direction);
  if (column === null || !isReportColumnKey(column)) return null;
  if (direction !== "asc" && direction !== "desc") return null;
  return { column, direction };
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item === "string" && item.trim().length > 0) out.push(item.trim());
  }
  return out;
}

function parseFilterSets(value: unknown): ReportScanFilterSets | null {
  const record = asRecord(value);
  if (record === null) return null;
  const dateSelection = parseDateSelection(record.dateSelection);
  const sort = parseSort(record.sort);
  if (dateSelection === null || sort === null) return null;

  const columnsRaw = parseStringArray(record.columns).filter(isReportColumnKey);
  const columns: readonly ReportColumnKey[] =
    columnsRaw.length > 0 ? columnsRaw : DEFAULT_REPORT_COLUMNS;

  const methods = parseStringArray(record.methods).filter(isReportScanMethod) as ReportScanMethod[];
  const lifecycleStates = parseStringArray(record.lifecycleStates).filter(
    isReportLifecycleState,
  ) as ReportScanLifecycleState[];

  return {
    siteNames: parseStringArray(record.siteNames),
    guardName: asString(record.guardName),
    routeName: asString(record.routeName),
    checkpointName: asString(record.checkpointName),
    methods,
    presenceAdhocOnly: asBoolean(record.presenceAdhocOnly) ?? false,
    lifecycleStates,
    reviewRequiredOnly: asBoolean(record.reviewRequiredOnly) ?? false,
    dateSelection,
    columns,
    sort: sort ?? DEFAULT_REPORT_SORT,
  };
}

function parseView(value: unknown): ReportSavedView | null {
  const record = asRecord(value);
  if (record === null) return null;
  const viewRef = asString(record.viewRef);
  const name = asString(record.name);
  const updatedAt = asString(record.updatedAt);
  const filters = parseFilterSets(record.filters);
  if (viewRef === null || name === null || updatedAt === null || filters === null) {
    return null;
  }
  return {
    viewRef,
    viewId: asString(record.viewId),
    name,
    filters,
    isDefault: asBoolean(record.isDefault) === true,
    updatedAt,
  };
}

function unavailableReason(
  value: unknown,
): "support_session" | "unavailable" {
  const record = asRecord(value);
  return record?.reason === "support_session" ? "support_session" : "unavailable";
}

function refusedReason(
  value: unknown,
): SaveReportViewResult & { status: "refused" } {
  const record = asRecord(value);
  const reason = asString(record?.reason);
  const allowed = [
    "name_taken",
    "view_limit",
    "payload_too_large",
    "unknown_column",
    "unknown_filter_value",
    "too_many_sites",
  ] as const;
  if (reason !== null && (allowed as readonly string[]).includes(reason)) {
    return {
      status: "refused",
      reason: reason as (typeof allowed)[number],
    };
  }
  return { status: "refused", reason: "unknown_filter_value" };
}

export function filtersToRpcPayload(filters: ReportScanFilterSets): Record<string, unknown> {
  return {
    siteNames: [...filters.siteNames],
    guardName: filters.guardName,
    routeName: filters.routeName,
    checkpointName: filters.checkpointName,
    methods: [...filters.methods],
    presenceAdhocOnly: filters.presenceAdhocOnly,
    lifecycleStates: [...filters.lifecycleStates],
    reviewRequiredOnly: filters.reviewRequiredOnly,
    dateSelection: filters.dateSelection,
    columns: [...filters.columns],
    sort: filters.sort,
  };
}

/**
 * Resolve opaque RV-XXXXXXXX locator back to a UUID when the host still stores UUIDs.
 * Host list responses may omit viewId; clients keep a local map when present.
 */
export function viewRefToUuidHint(viewRef: string): string | null {
  const match = /^RV-([0-9A-F]{8})$/i.exec(viewRef.trim());
  if (match === null) return null;
  return null;
}

export async function listMyReportViews(
  client: RpcClient,
  accountId: string,
): Promise<ReportSavedViewList> {
  const { data, error } = await client.rpc("list_my_report_views", {
    p_account_id: accountId,
  });
  if (error !== null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  const record = asRecord(data);
  if (record === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  if (record.status === "unavailable") {
    return { status: "unavailable", reason: unavailableReason(record) };
  }
  if (record.status !== "ok" || !Array.isArray(record.views)) {
    return { status: "unavailable", reason: "unavailable" };
  }
  const views: ReportSavedView[] = [];
  for (const item of record.views) {
    const view = parseView(item);
    if (view === null) {
      return { status: "unavailable", reason: "unavailable" };
    }
    views.push(view);
  }
  return { status: "ok", views };
}

export async function upsertMyReportView(
  client: RpcClient,
  input: {
    readonly accountId: string;
    readonly name: string;
    readonly filters: ReportScanFilterSets;
    readonly viewId?: string | null;
    readonly makeDefault?: boolean;
  },
): Promise<SaveReportViewResult> {
  const { data, error } = await client.rpc("upsert_my_report_view", {
    p_account_id: input.accountId,
    p_name: input.name,
    p_payload: filtersToRpcPayload(input.filters),
    p_view_id: input.viewId ?? null,
    p_make_default: input.makeDefault === true,
  });
  if (error !== null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  const record = asRecord(data);
  if (record === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  if (record.status === "unavailable") {
    return { status: "unavailable", reason: unavailableReason(record) };
  }
  if (record.status === "refused") {
    return refusedReason(record);
  }
  if (record.status !== "ok") {
    return { status: "unavailable", reason: "unavailable" };
  }
  const view = parseView(record.view);
  if (view === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  return { status: "ok", view };
}

export async function renameMyReportView(
  client: RpcClient,
  input: { readonly accountId: string; readonly viewId: string; readonly name: string },
): Promise<SaveReportViewResult> {
  const { data, error } = await client.rpc("rename_my_report_view", {
    p_account_id: input.accountId,
    p_view_id: input.viewId,
    p_name: input.name,
  });
  if (error !== null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  const record = asRecord(data);
  if (record === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  if (record.status === "unavailable") {
    return { status: "unavailable", reason: unavailableReason(record) };
  }
  if (record.status === "refused") {
    return refusedReason(record);
  }
  const view = parseView(record.view);
  if (view === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  return { status: "ok", view };
}

export async function deleteMyReportView(
  client: RpcClient,
  input: { readonly accountId: string; readonly viewId: string },
): Promise<DeleteReportViewResult> {
  const { data, error } = await client.rpc("delete_my_report_view", {
    p_account_id: input.accountId,
    p_view_id: input.viewId,
  });
  if (error !== null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  const record = asRecord(data);
  if (record === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  if (record.status === "unavailable") {
    return { status: "unavailable", reason: unavailableReason(record) };
  }
  if (record.status !== "ok") {
    return { status: "unavailable", reason: "unavailable" };
  }
  return { status: "ok" };
}

export async function setMyDefaultReportView(
  client: RpcClient,
  input: { readonly accountId: string; readonly viewId: string },
): Promise<SaveReportViewResult> {
  const { data, error } = await client.rpc("set_my_default_report_view", {
    p_account_id: input.accountId,
    p_view_id: input.viewId,
  });
  if (error !== null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  const record = asRecord(data);
  if (record === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  if (record.status === "unavailable") {
    return { status: "unavailable", reason: unavailableReason(record) };
  }
  const view = parseView(record.view);
  if (view === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  return { status: "ok", view };
}

export async function validateMyReportViewLabels(
  client: RpcClient,
  input: {
    readonly accountId: string;
    readonly labels: {
      readonly siteNames?: readonly string[];
      readonly guardName?: string | null;
      readonly routeName?: string | null;
      readonly checkpointName?: string | null;
    };
  },
): Promise<ReportLabelValidation> {
  const { data, error } = await client.rpc("validate_my_report_view_labels", {
    p_account_id: input.accountId,
    p_labels: {
      siteNames: [...(input.labels.siteNames ?? [])],
      guardName: input.labels.guardName ?? null,
      routeName: input.labels.routeName ?? null,
      checkpointName: input.labels.checkpointName ?? null,
    },
  });
  if (error !== null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  const record = asRecord(data);
  if (record === null) {
    return { status: "unavailable", reason: "unavailable" };
  }
  if (record.status === "unavailable") {
    return { status: "unavailable", reason: unavailableReason(record) };
  }
  if (record.status === "stale") {
    const unmatched: { family: string; label: string }[] = [];
    if (Array.isArray(record.unmatched)) {
      for (const item of record.unmatched) {
        const row = asRecord(item);
        const family = asString(row?.family);
        const label = asString(row?.label);
        if (family !== null && label !== null) {
          unmatched.push({ family, label });
        }
      }
    }
    return { status: "stale", unmatched };
  }
  if (record.status !== "ok") {
    return { status: "unavailable", reason: "unavailable" };
  }
  return { status: "ok" };
}
